import { IAssistantWalkthroughFeatureModel } from "api/services/chat/assistant";

import {
  ASSISTANT_COMPLETION_EVENT,
  AssistantCompletionType,
  EMPLOYEE_CATEGORY_CONSTANT,
  IIdToken,
  RoutePaths,
  ScopeEnum,
} from "config";

import { AssistantScenarioStatusEnum } from "./enums";
import { AssistantGuide, IAssistantPosition } from "./types";
import {
  assistantPages,
  homepageResources,
  ASSISTANT_WALKTHROUGH_QUERY_PARAM,
  ASSISTANT_FEATURE_QUERY_PARAM,
} from "./constants";

export function getAssistantPage(pathname: string, token?: IIdToken) {
  const entry = assistantPages.find(
    ({ page }) =>
      pathname === page ||
      (page !== RoutePaths.HOME && pathname.startsWith(`${page}/`))
  );

  if (!entry) return undefined;

  const isHomepage = entry.page === RoutePaths.HOME;

  if (isHomepage && token?.category !== EMPLOYEE_CATEGORY_CONSTANT) {
    return undefined;
  }

  const hasAccess = token?.permissions?.some(({ resource, scopes }) => {
    const matchesResource = isHomepage
      ? homepageResources.some(
          (homepageResource) => homepageResource === resource
        )
      : resource === entry.resource;

    return (
      matchesResource &&
      (scopes.includes(ScopeEnum.View) ||
        (isHomepage && scopes.includes(ScopeEnum.Create)))
    );
  });

  return hasAccess ? entry.page : undefined;
}

export function emitAssistantCompletion(action: AssistantCompletionType): void {
  window.dispatchEvent(
    new CustomEvent<AssistantCompletionType>(ASSISTANT_COMPLETION_EVENT, {
      detail: action,
    })
  );
}

export function isAssistantTargetVisible(element: HTMLElement): boolean {
  if (element.closest('[aria-hidden="true"], [inert], [hidden]')) return false;

  const rect = element.getBoundingClientRect();

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    getComputedStyle(element).visibility !== "hidden"
  );
}

export function findAssistantTarget(target: string): HTMLElement | undefined {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-assistant-target~="${target}"]`
    )
  ).find(isAssistantTargetVisible);
}

export function clampAssistantPosition(
  position: IAssistantPosition,
  bounds: Pick<DOMRect, "width" | "height">,
  gap: number
): IAssistantPosition {
  return {
    left: Math.min(
      Math.max(gap, position.left),
      Math.max(gap, window.innerWidth - bounds.width - gap)
    ),
    top: Math.min(
      Math.max(gap, position.top),
      Math.max(gap, window.innerHeight - bounds.height - gap)
    ),
  };
}

export function isAssistantPosition(
  value: unknown
): value is IAssistantPosition {
  if (!value || typeof value !== "object") return false;

  return (
    "left" in value &&
    typeof value.left === "number" &&
    Number.isFinite(value.left) &&
    "top" in value &&
    typeof value.top === "number" &&
    Number.isFinite(value.top)
  );
}

export function getAssistantWalkthroughUrl(
  guide: AssistantGuide,
  walkthroughFeatureId?: string
): string | undefined {
  if (!assistantPages.some(({ page }) => page === guide.page)) return undefined;

  const params = new URLSearchParams(
    walkthroughFeatureId
      ? { [ASSISTANT_FEATURE_QUERY_PARAM]: walkthroughFeatureId }
      : { [ASSISTANT_WALKTHROUGH_QUERY_PARAM]: guide.id }
  );

  return `${guide.page}?${params.toString()}`;
}

export function getAssistantScenarioCounts(
  scenarios: IAssistantWalkthroughFeatureModel["scenarios"]
) {
  return scenarios.reduce(
    (counts, scenario) => ({
      completed:
        counts.completed +
        (scenario.status === AssistantScenarioStatusEnum.COMPLETED ? 1 : 0),
      skipped:
        counts.skipped +
        (scenario.status === AssistantScenarioStatusEnum.SKIPPED ? 1 : 0),
    }),
    { completed: 0, skipped: 0 }
  );
}

export function getAssistantScreen(
  guide: AssistantGuide | undefined,
  index: number,
  feature: IAssistantWalkthroughFeatureModel | undefined,
  finished: boolean
): string {
  if (guide) return `guide:${guide.id}:${index}`;

  if (feature) return `feature:${feature.id}:${finished}`;

  return "conversation";
}
